import React from 'react'
import { Typography } from '@material-ui/core'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'

import palette from '../../../../themes/palette'
import type { RunResult as Result } from '../../../../api/EmailTestBenchApi'
import {
  FAILED_TITLE,
  LABEL_FROM,
  LABEL_REPLY_TO,
  LABEL_SUBJECT,
  LABEL_TEMPLATE,
  LABEL_TO,
  PREVIEW_CAVEAT,
  RECEIPT,
  REFUSED_TITLE,
  SENT_BODY,
  SENT_NO_RECEIPT_BODY,
  SENT_NO_RECEIPT_TITLE,
  SENT_TITLE,
  UNAVAILABLE_TITLE,
  UNSUPPORTED_TITLE,
  WARNINGS_TITLE,
  WOULD_HAVE_GONE_TO
} from './copy'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    panel: {
      border: `1px solid ${palette.lightestgrey}`,
      borderRadius: 8,
      padding: theme.spacing(1.5, 2),
      marginTop: theme.spacing(1)
    },
    ok: { borderColor: palette.teal },
    bad: { borderColor: palette.red },
    warn: { borderColor: palette.darkYellow },
    title: { fontWeight: 600, marginBottom: theme.spacing(0.5) },
    titleOk: { color: palette.teal },
    titleBad: { color: palette.red },
    titleWarn: { color: palette.darkYellow },
    meta: {
      fontFamily: 'monospace',
      fontSize: 11.5,
      color: palette.grey,
      marginTop: theme.spacing(1),
      overflowX: 'auto',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word'
    },
    frame: {
      border: `1px solid ${palette.lightestgrey}`,
      borderRadius: 8,
      overflow: 'hidden',
      marginTop: theme.spacing(1)
    },
    frameHeader: {
      padding: theme.spacing(1.25, 1.75),
      borderBottom: `1px solid ${palette.lightestgrey}`,
      backgroundColor: palette.nothinGrey,
      fontFamily: 'monospace',
      fontSize: 11.5,
      color: palette.darkergrey,
      lineHeight: 1.7,
      overflowX: 'auto'
    },
    iframe: { display: 'block', border: 0, width: '100%', height: 460, backgroundColor: palette.white },
    caveat: { marginTop: theme.spacing(1), display: 'block' },
    list: { margin: 0, paddingLeft: theme.spacing(2.5) }
  })
)

const Panel: React.FC<{ tone: 'ok' | 'bad' | 'warn'; title: string; id?: string }> = ({
  tone,
  title,
  id,
  children
}) => {
  const classes = useStyles()
  const toneClass = tone === 'ok' ? classes.ok : tone === 'bad' ? classes.bad : classes.warn
  const titleTone = tone === 'ok' ? classes.titleOk : tone === 'bad' ? classes.titleBad : classes.titleWarn

  return (
    <div className={`${classes.panel} ${toneClass}`} id={id}>
      <Typography variant='body2' className={`${classes.title} ${titleTone}`}>
        {title}
      </Typography>
      {children}
    </div>
  )
}

/**
 * Everything that can come back from "Show me" or "Send it to me".
 *
 * 🔴 NEVER A TRACEBACK AND NEVER A RUBY CLASS NAME. The server already refuses
 * to send one -- a rendering failure is logged in full there and arrives here as
 * a single sentence -- and nothing in this component reintroduces it. That is
 * not politeness: `FloatDomainError: NaN` on screen is how a tool teaches its
 * user that it is broken and they are helpless.
 */
const RunResult: React.FC<{ result: Result }> = ({ result }) => {
  const classes = useStyles()

  const reasons = (
    <ul className={classes.list}>
      {(result.reasons || []).map((reason) => (
        <li key={reason}>
          <Typography variant='body2' component='span'>
            {reason}
          </Typography>
        </li>
      ))}
    </ul>
  )

  if (result.status === 'refused') {
    return (
      <Panel tone='bad' title={REFUSED_TITLE} id='ic-bench-result-refused'>
        {reasons}
      </Panel>
    )
  }

  if (result.status === 'unsupported') {
    return (
      <Panel tone='warn' title={UNSUPPORTED_TITLE} id='ic-bench-result-unsupported'>
        {reasons}
      </Panel>
    )
  }

  if (result.status === 'unavailable' || result.status === 'unknown_mailer') {
    return (
      <Panel tone='warn' title={UNAVAILABLE_TITLE} id='ic-bench-result-unavailable'>
        {reasons}
      </Panel>
    )
  }

  if (result.status === 'failed') {
    return (
      <Panel tone='bad' title={FAILED_TITLE} id='ic-bench-result-failed'>
        {reasons}
      </Panel>
    )
  }

  if (result.status === 'sent') {
    const noReceipt = !!result.no_receipt
    return (
      <Panel
        tone={noReceipt ? 'warn' : 'ok'}
        title={noReceipt ? SENT_NO_RECEIPT_TITLE : SENT_TITLE}
        id='ic-bench-result-sent'
      >
        <Typography variant='body2'>
          {noReceipt ? SENT_NO_RECEIPT_BODY : SENT_BODY(result.recipient || '')}
        </Typography>
        <div className={classes.meta}>
          {result.marked_subject && (
            <div>
              {LABEL_SUBJECT}: {result.marked_subject}
            </div>
          )}
          {!!result.would_have_gone_to?.length && (
            <div>
              {WOULD_HAVE_GONE_TO}: {result.would_have_gone_to.join(', ')}
            </div>
          )}
          {(result.receipts || []).map((receipt) => (
            <div key={receipt.id}>
              {RECEIPT} #{receipt.id} · {receipt.template} · status={receipt.status}
              {receipt.reject_reason ? ` · ${receipt.reject_reason}` : ''}
            </div>
          ))}
        </div>
        <Warnings warnings={result.warnings} />
      </Panel>
    )
  }

  // Previewed.
  const message = result.message
  return (
    <div id='ic-bench-result-preview'>
      <div className={classes.frame}>
        <div className={classes.frameHeader}>
          <div>
            {LABEL_TO} {(message?.to || []).join(', ')}
          </div>
          <div>
            {LABEL_FROM} {message?.from}
          </div>
          <div>
            {LABEL_SUBJECT} {message?.subject}
          </div>
          <div>
            {LABEL_TEMPLATE} {message?.template_slug}
          </div>
          {message?.reply_to && (
            <div>
              {LABEL_REPLY_TO} {message.reply_to}
            </div>
          )}
        </div>
        {/*
          srcDoc with an empty sandbox: the merge vars are app-generated HTML,
          but they are rendered from partner-authored content, and a preview
          pane is not a place to run it. Empty sandbox = no scripts, no forms,
          no navigation, no same-origin.
        */}
        <iframe
          title='Email preview'
          className={classes.iframe}
          sandbox=''
          srcDoc={result.html || ''}
        />
      </div>
      {/*
        The honest caveat, and not a footnote. "Show me" renders the SOURCE --
        Mandrill wraps it in the template, and Gmail and Outlook mangle the
        result differently. A clean preview does not mean the email is fine, and
        letting the page imply otherwise defeats the point of sending it.
      */}
      <Typography variant='caption' color='textSecondary' className={classes.caveat}>
        {PREVIEW_CAVEAT}
      </Typography>
      <Warnings warnings={result.warnings} />
    </div>
  )
}

/**
 * Registry's own caveats -- synthesised tokens whose links will not resolve, a
 * blank sign-off, a mailer that omits merge_language. Shown so nobody reports a
 * known defect as a new one.
 */
const Warnings: React.FC<{ warnings?: string[] }> = ({ warnings }) => {
  const classes = useStyles()
  if (!warnings || warnings.length === 0) return null

  return (
    <div className={classes.meta}>
      <div>{WARNINGS_TITLE}</div>
      <ul className={classes.list}>
        {warnings.map((warning) => (
          <li key={warning}>{warning}</li>
        ))}
      </ul>
    </div>
  )
}

export default RunResult
